import 'reflect-metadata'

import { Expose, Transform } from 'class-transformer'

import { logger } from '../'
import { Language } from '../lang/Language'
import { BetKind } from './Bet'
import { Board } from './Board'

enum BetHintKind {
  allin = 'allin',
  fractionofpot = 'fractionofpot',
  multiplesofbigblind = 'multiplesofbigblind',
  multiplesoflastbet = 'multiplesoflastbet'
}

class BetHint {
  amount: number

  @Expose()
  @Transform(({ value }) => BetHintKind[value.toLowerCase() as keyof typeof BetHintKind])
  kind: BetHintKind
  label: string

  constructor(amount: number, kind: BetHintKind, label: string) {
    this.amount = amount
    this.kind = kind
    this.label = label
  }
}

type BetRecommendationValue = number
type BetRecommendationCopy =
  | (typeof Language.strings.betHints)['fractionOfPot'][keyof (typeof Language.strings.betHints)['fractionOfPot']]
  | (typeof Language.strings.betHints)['multipleOfBet'][keyof (typeof Language.strings.betHints)['multipleOfBet']]
  | (typeof Language.strings.betHints)['multipleOfBlind'][keyof (typeof Language.strings.betHints)['multipleOfBlind']]
type BetValueInfo = [BetRecommendationValue, BetRecommendationCopy]

class BettingRecommender {
  private static multipliersOfBlind: Array<BetValueInfo> = [
    [2, Language.strings.betHints.multipleOfBlind.twoTimes],
    [3, Language.strings.betHints.multipleOfBlind.threeTimes],
    [4, Language.strings.betHints.multipleOfBlind.fourTimes]
  ]

  private static multipliersOfBet: Array<BetValueInfo> = [
    [2, Language.strings.betHints.multipleOfBet.twoTimes],
    [3, Language.strings.betHints.multipleOfBet.threeTimes],
    [4, Language.strings.betHints.multipleOfBet.fourTimes]
  ]

  private static fractionsOfPot: Array<BetValueInfo> = [
    [1 / 3, Language.strings.betHints.fractionOfPot.oneThird],
    [0.5, Language.strings.betHints.fractionOfPot.oneHalf],
    [0.75, Language.strings.betHints.fractionOfPot.threeQuarters],
    [1, Language.strings.betHints.fractionOfPot.fullPot]
  ]

  /**
   * Get bet hints populated based on board state and existing
   *  bets
   *
   * @returns bet hints
   */
  static getBetHints(board: Board): BetHint[] {
    if (board.isPreDeal() || board.isShowdown()) {
      return []
    }
    if (board.isDeal()) {
      return BettingRecommender.getHintsBeforeAction(board)
    }
    return BettingRecommender.getBetsAfterAction(board)
  }

  /**
   * Pre-flop bets start with multiples of the big blind
   *  Once the first bet has been made, recommendations suggest
   *  multiples of the last bet (2x, 3x, 4x)
   *
   * @returns bet hints
   */
  static getHintsBeforeAction(board: Board): BetHint[] {
    if (board.hasBetsNoChecks()) {
      return BettingRecommender.getMultiplesOfLastBet(board)
    }
    return BettingRecommender.getMultiplesOfBigBlind(board)
  }

  /**
   * After any action to the board (flop, turn, river),
   *  if the board has no bets, show fractions of the pot
   *  if the board has bets, show multiples of the biggest bet
   *   including raises.
   *
   * @returns bet hints
   */
  static getBetsAfterAction(board: Board): BetHint[] {
    if (board.hasBetsNoChecks()) {
      return BettingRecommender.getMultiplesOfLastBet(board)
    }
    return BettingRecommender.getFractionsOfPot(board)
  }

  /**
   * Creates BetHints from the wrapped board's big blind
   *
   * @returns bet hints
   */
  static getMultiplesOfBigBlind(board: Board) {
    return BettingRecommender.multipliersOfBlind.map(
      (multiplier) =>
        <BetHint>{
          kind: BetHintKind.multiplesofbigblind,
          amount: Math.floor(multiplier[0] * board.blinds.big),
          label: multiplier[1]
        }
    )
  }

  /**
   * Creates BetHints from the wrapped boards last bet
   *
   * @returns bet hints
   */
  static getMultiplesOfLastBet(board: Board) {
    return BettingRecommender.multipliersOfBet.map(
      (multiplier) =>
        <BetHint>{
          kind: BetHintKind.multiplesoflastbet,
          amount: Math.floor(multiplier[0] * board.lastBet()),
          label: multiplier[1]
        }
    )
  }

  /**
   * Creates BetHints from the wrapped board's pot
   *
   * @returns bet hints
   */
  static getFractionsOfPot(board: Board) {
    return BettingRecommender.fractionsOfPot
      .map((fractional) => {
        return <BetHint>{
          kind: BetHintKind.fractionofpot,
          amount: Math.floor(fractional[0] * board.getTotalPotsValue()),
          label: fractional[1]
        }
      })
      .filter((betHint) => betHint.amount >= board.blinds.big)
  }
}

const BetValidationErrors = {
  minimunRaiseError: 'minimunRaiseError',
  betLowerThanMinimunRaiseError: 'betLowerThanMinimunRaiseError',
  betBiggerThanChipStackError: 'betBiggerThanChipStackError',
  betLowerThanBigBlindError: 'betLowerThanBigBlindError',
  betLowerThanBiggestBetError: 'betLowerThanBiggestBetError',
  betIsNotCallAmount: 'betIsNotCallAmount'
} as const
type BetValidationError = keyof typeof BetValidationErrors

type ValidFxResult =
  | {
      ok: true
    }
  | {
      ok: false
      error: BetValidationError
      info: Record<string, unknown>
    }

type ValidFxInputs = {
  bigBlind: number
  biggestBet: number
  playerAlreadyBet: number
  playerChips: number
  betAmount: number
  callAmount: number
  minimumRaise: number | undefined
}

class BettingValidator {
  static Validations: Record<BetKind, ((inputs: ValidFxInputs) => ValidFxResult)[]> = {
    [BetKind.ALLIN]: [BettingValidator.betAmountIsLessThanPlayersTotalChips],
    [BetKind.CALL]: [BettingValidator.betAmountIsLessThanPlayersTotalChips, BettingValidator.betAmountIsCallAmount],
    [BetKind.BET]: [
      BettingValidator.betAmountIsGreaterThanMinimumRaise,
      BettingValidator.betAmountIsLessThanPlayersTotalChips,
      BettingValidator.betAmountIsAsBigAsBigBlindBet,
      BettingValidator.betAmountIsAsBigAsBiggestBet
    ],
    [BetKind.RAISE]: [
      BettingValidator.betAmountIsGreaterThanMinimumRaise,
      BettingValidator.betAmountIsLessThanPlayersTotalChips,
      BettingValidator.betAmountIsAsBigAsBigBlindBet,
      BettingValidator.betAmountIsAsBigAsBiggestBet
    ],
    [BetKind.FOLD]: [],
    [BetKind.BIGBLIND]: [],
    [BetKind.SMALLBLIND]: [],
    [BetKind.BLANK]: [],
    [BetKind.CHECK]: []
  }

  /**
   * Returns true if the betAmount is a valid bet for this player on this
   * board. Returns false otherwise
   */
  static isValidBetForPlayer(inputs: ValidFxInputs, kind: BetKind) {
    // Bet Kinds that don't need validations
    if ([BetKind.SMALLBLIND, BetKind.BIGBLIND, BetKind.BLANK].includes(kind)) return { ok: true as const }

    // All in is always a valid bet for a player
    if (inputs.betAmount === inputs.playerAlreadyBet + inputs.playerChips) return { ok: true as const }

    const validations = BettingValidator.Validations[kind]

    const validationResults = validations.map((validFx) => validFx(inputs))

    const firstError = validationResults.find((result) => !result.ok)
    if (firstError && !firstError.ok) return firstError
    return { ok: true as const }
  }

  static betAmountIsGreaterThanMinimumRaise({
    betAmount,
    minimumRaise,
    playerChips,
    playerAlreadyBet
  }: ValidFxInputs): ValidFxResult {
    if (!minimumRaise && minimumRaise !== 0) {
      return { ok: false, error: BetValidationErrors.minimunRaiseError, info: {} }
    }
    if (betAmount + playerAlreadyBet >= minimumRaise) return { ok: true as const }
    if (betAmount === playerChips) return { ok: true as const }

    logger.error(`${BetValidationErrors.betLowerThanMinimunRaiseError} ${minimumRaise}`)
    return {
      ok: false as const,
      error: BetValidationErrors.betLowerThanMinimunRaiseError,
      info: { betAmount, minimumRaise, playerChips, playerAlreadyBet }
    }
  }

  static betAmountIsLessThanPlayersTotalChips({
    playerAlreadyBet,
    playerChips,
    betAmount
  }: ValidFxInputs): ValidFxResult {
    if (betAmount <= playerAlreadyBet + playerChips) {
      return { ok: true as const }
    }
    logger.error(
      `${BetValidationErrors.betBiggerThanChipStackError} bet=${betAmount} chips=${playerAlreadyBet + playerChips}`
    )
    return {
      ok: false as const,
      error: BetValidationErrors.betBiggerThanChipStackError,
      info: { betAmount, playerChips, playerAlreadyBet }
    }
  }

  static betAmountIsAsBigAsBigBlindBet({
    bigBlind,
    playerChips,
    betAmount,
    playerAlreadyBet
  }: ValidFxInputs): ValidFxResult {
    if (playerChips >= bigBlind && betAmount + playerAlreadyBet >= bigBlind) {
      return { ok: true as const }
    }
    logger.error(`${BetValidationErrors.betLowerThanBigBlindError} ${bigBlind}`)
    return {
      ok: false as const,
      error: BetValidationErrors.betLowerThanBigBlindError,
      info: { bigBlind, betAmount, playerChips, playerAlreadyBet }
    }
  }

  static betAmountIsAsBigAsBiggestBet({
    biggestBet,
    betAmount,
    playerChips,
    playerAlreadyBet
  }: ValidFxInputs): ValidFxResult {
    if (biggestBet <= 0) return { ok: true as const }
    if (betAmount === playerChips) return { ok: true as const }
    if (betAmount + playerAlreadyBet >= biggestBet) return { ok: true as const }

    logger.error(`${BetValidationErrors.betLowerThanBiggestBetError} ${biggestBet}`)
    return {
      ok: false as const,
      error: BetValidationErrors.betLowerThanBiggestBetError,
      info: { biggestBet, betAmount, playerChips, playerAlreadyBet }
    }
  }

  static betAmountIsCallAmount({ betAmount, callAmount }: ValidFxInputs): ValidFxResult {
    if (betAmount === callAmount) {
      return { ok: true as const }
    }
    logger.error(`${BetValidationErrors.betIsNotCallAmount} ${betAmount}`)
    return { ok: false as const, error: BetValidationErrors.betIsNotCallAmount, info: { betAmount, callAmount } }
  }
}

export { BetHint, BetHintKind, BettingRecommender, BettingValidator }
